/**
 * ColumnStickerRendererPlugin — `column.sticker` renderer.
 *
 * Default (plain column layout) presentation for sticker source
 * rows: the column's stickers as a vertical list through the shared
 * `ListRenderer`/`ListCell` chrome — the sticker counterpart of
 * `column.event-list`. `toProps` reads the row's custom cells
 * (`{stickerId, message}` keyed by a DateTime range), mapping each into
 * the `ListItem` viewmodel (no accent bar; `at` from the cell's range start).
 *
 * The timeline/day-grid presentation (one custom cell per sticker
 * with move/details gestures) stays on the source plugin
 * (`source-sticker.ts`); this renderer only owns the column slot.
 */

import { DateTime } from "@tento-chrono";
import { LonaIcons, SparkleIcon } from "@tento-ui/ui/icons";
import { EntryList } from "@tento-ui/components/entry-list/entry-list";
import type { ListItem } from "@tento-ui/components/entry-list/list-item";
import type { BindCellContext, RowOptionsElement } from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { COLUMN_STICKER_SPEC } from "@tento-lona/sheets/platform";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import { ListCell } from "./list-cell.ts";
import { defaultListDisplay, ListRenderer } from "./list-renderer.ts";
import { parseStickerData } from "./source-sticker/sticker-cell-data.ts";

function stickerTitle(data: { stickerId: string; message: string }): string {
  return data.message.length > 0 ? data.message : data.stickerId;
}

/** The renderer plugin that lists a row's stickers per column. */
export class ColumnStickerRendererPlugin extends ListRenderer {
  static readonly spec: PerTypeSpec = COLUMN_STICKER_SPEC;

  readonly id: string = "column.sticker";
  readonly icon: HTMLTemplateElement = LonaIcons.$template(SparkleIcon);

  constructor(makeRowOptions: RowOptionsElement.Factory) {
    super(makeRowOptions, { toProps: stickerListItems, display: defaultListDisplay });
  }

  // Stickers render on a standalone sheet cell (not an already-backed
  // context like tasks), so paint the card surface.
  makeCell(): EntryList {
    const $cell = ListCell.make();
    ListCell.applySurface($cell);
    return $cell;
  }
}

/** The row's sticker cells in the column's range, as list items (earliest first). */
async function stickerListItems({
  row,
  column,
}: BindCellContext): Promise<ListItem[]> {
  const pd = column.discrete()[0];
  const local = column.tz;
  const start = local.toWallClock(pd.start.withTime()).toUtc();
  const end = local.toWallClock(pd.end.withTime()).toUtc();
  const utcRange = new DateTime.Range(start, end);

  const customCells = (await row.cells(utcRange)).filter((c) => c.kind === "timepoint");

  const items: ListItem[] = [];
  for (const cell of customCells) {
    const data = parseStickerData(cell.value);
    if (!data) continue;
    items.push({
      id: cell.id,
      title: stickerTitle(data),
      isEditable: false,
      at: cell.dtr.start,
      timezone: local,
    });
  }
  items.sort((a, b) => (a.at?.mse ?? 0) - (b.at?.mse ?? 0));
  return items;
}
